import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { influencers, distributors } from "@/mocks/products";

export default function InfluencerSection() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"influencer" | "distributor" | "kol">("influencer");

  return (
    <section className="py-10 bg-gradient-to-br from-red-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Noto Serif SC', serif" }}>
              网红带货 &amp; 分销合作
            </h2>
            <p className="text-gray-500 text-sm mt-1">顶级主播直播带货 · 全国分销商合作招募 · 达人代发货赚佣金</p>
          </div>
          <div className="flex items-center bg-white rounded-full p-1 border border-gray-200">
            <button
              onClick={() => setActiveTab("influencer")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === "influencer" ? "bg-red-500 text-white" : "text-gray-500 hover:text-red-500"}`}
            >
              网红主播
            </button>
            <button
              onClick={() => setActiveTab("distributor")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === "distributor" ? "bg-red-500 text-white" : "text-gray-500 hover:text-red-500"}`}
            >
              分销商
            </button>
            <button
              onClick={() => setActiveTab("kol")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === "kol" ? "bg-gradient-to-r from-amber-500 to-yellow-400 text-white" : "text-gray-500 hover:text-amber-500"}`}
            >
              <i className="ri-vip-crown-line mr-1"></i>达人赚佣金
            </button>
          </div>
        </div>

        {/* Influencer cards */}
        {activeTab === "influencer" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {influencers.map((inf) => (
              <div
                key={inf.id}
                className="bg-white rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform duration-200"
              >
                <div className="relative">
                  <img
                    src={inf.avatar}
                    alt={inf.name}
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-bold text-lg">{inf.name}</p>
                        <p className="text-white/80 text-xs">{inf.title}</p>
                      </div>
                      <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                        {inf.badge}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-center">
                      <p className="text-red-500 font-bold text-sm">{inf.followers}</p>
                      <p className="text-gray-400 text-xs">粉丝数</p>
                    </div>
                    <div className="w-px h-8 bg-gray-100"></div>
                    <div className="text-center">
                      <p className="text-red-500 font-bold text-sm">{inf.sales}</p>
                      <p className="text-gray-400 text-xs">销售额</p>
                    </div>
                    <div className="w-px h-8 bg-gray-100"></div>
                    <div className="text-center">
                      <p className="text-gray-700 font-bold text-sm">{inf.specialty}</p>
                      <p className="text-gray-400 text-xs">专长</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {inf.products.map((p) => (
                      <span key={p} className="bg-red-50 text-red-500 text-xs px-2 py-0.5 rounded-full">
                        {p}
                      </span>
                    ))}
                  </div>
                  <button className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-live-line mr-1"></i> 进入直播间
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Distributor cards */}
        {activeTab === "distributor" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {distributors.map((dist) => (
              <div
                key={dist.id}
                className="bg-white rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform duration-200"
              >
                <div className={`bg-gradient-to-r ${dist.color} p-6 text-white`}>
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full mb-3">
                    <i className={`${dist.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold">{dist.name}</h3>
                  <p className="text-white/80 text-sm mt-1">{dist.region} · {dist.level}</p>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center bg-gray-50 rounded-lg p-3">
                      <p className="text-red-500 font-bold text-lg">{dist.commission}</p>
                      <p className="text-gray-400 text-xs mt-0.5">佣金比例</p>
                    </div>
                    <div className="text-center bg-gray-50 rounded-lg p-3">
                      <p className="text-red-500 font-bold text-lg">{dist.members}</p>
                      <p className="text-gray-400 text-xs mt-0.5">分销成员</p>
                    </div>
                  </div>
                  <button className="w-full border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                    申请加入
                  </button>
                </div>
              </div>
            ))}
            {/* CTA card */}
            <div className="bg-gradient-to-br from-red-500 to-red-700 rounded-xl p-6 text-white flex flex-col justify-between cursor-pointer hover:scale-[1.02] transition-transform duration-200">
              <div>
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full mb-3">
                  <i className="ri-store-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">成为皇冠分销商</h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  加入皇冠鸿运商城分销体系，享受高额佣金，专属培训，全程运营支持
                </p>
              </div>
              <button className="mt-6 bg-white text-red-500 py-2.5 rounded-lg text-sm font-bold transition-colors cursor-pointer whitespace-nowrap hover:bg-red-50">
                立即申请 <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        )}

        {/* KOL Dropshipping Commission Tab */}
        {activeTab === "kol" && (
          <div>
            {/* Hero banner */}
            <div className="relative rounded-2xl overflow-hidden mb-6">
              <img
                src="https://public.readdy.ai/ai/img_res/741e92c2300bee5193cc3cc809ec8363.jpg"
                alt="达人分销"
                className="w-full h-48 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>
              <div className="absolute inset-0 flex items-center px-8">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs px-3 py-1 rounded-full font-bold">
                      <i className="ri-vip-crown-line mr-1"></i>达人专属
                    </span>
                  </div>
                  <h3 className="text-white font-bold text-2xl mb-1" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                    抖音 · 微信 · TikTok · YouTube 达人
                  </h3>
                  <p className="text-white/80 text-sm mb-4">
                    推广工厂商品，工厂直接代发货，你只需带货 — 每单按工厂设定比例赚佣金
                  </p>
                  <button
                    onClick={() => navigate("/influencer-dashboard")}
                    className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white px-6 py-2.5 rounded-xl font-bold text-sm transition-all hover:opacity-90 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-bar-chart-line mr-1"></i>进入达人佣金中心
                  </button>
                </div>
              </div>
            </div>

            {/* How it works */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {[
                {
                  step: "1",
                  icon: "ri-megaphone-line",
                  title: "选品 · 发布内容",
                  desc: "从 2,400+ 家工厂商品库中选品，在抖音、TikTok、微信视频号、YouTube 发布推广内容",
                  color: "from-amber-400 to-yellow-500",
                },
                {
                  step: "2",
                  icon: "ri-truck-line",
                  title: "工厂本地仓直发",
                  desc: "粉丝下单后，工厂从本地仓库直接发货（Dropshipping），你无需备货，零库存风险",
                  color: "from-orange-400 to-amber-500",
                },
                {
                  step: "3",
                  icon: "ri-money-cny-circle-line",
                  title: "按比例结算佣金",
                  desc: "每笔订单按工厂自主设定的佣金比例（8%–20%）自动计算，每月1日统一到账",
                  color: "from-yellow-400 to-amber-400",
                },
              ].map((item) => (
                <div key={item.step} className="bg-white rounded-xl p-5">
                  <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-br ${item.color} rounded-xl mb-3`}>
                    <i className={`${item.icon} text-white text-xl`}></i>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-amber-500 font-bold text-xs">STEP {item.step}</span>
                  </div>
                  <h4 className="text-gray-800 font-bold text-sm mb-1">{item.title}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Platform cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                { name: "抖音", icon: "ri-tiktok-line", color: "bg-gray-900", desc: "国内最大短视频平台", commission: "最高 20%" },
                { name: "微信视频号", icon: "ri-wechat-line", color: "bg-green-600", desc: "熟人社交高转化", commission: "最高 18%" },
                { name: "TikTok", icon: "ri-tiktok-line", color: "bg-gray-700", desc: "全球短视频平台", commission: "最高 20%" },
                { name: "YouTube", icon: "ri-youtube-line", color: "bg-red-600", desc: "全球最大视频平台", commission: "最高 15%" },
              ].map((p) => (
                <div key={p.name} className="bg-white rounded-xl p-4 text-center">
                  <div className={`w-12 h-12 flex items-center justify-center ${p.color} rounded-xl mx-auto mb-3`}>
                    <i className={`${p.icon} text-white text-xl`}></i>
                  </div>
                  <p className="text-gray-800 font-bold text-sm">{p.name}</p>
                  <p className="text-gray-400 text-xs mt-0.5 mb-2">{p.desc}</p>
                  <span className="bg-amber-100 text-amber-600 text-xs px-2 py-0.5 rounded-full font-bold">
                    {p.commission}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-amber-500 to-yellow-400 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-white">
                <h3 className="font-bold text-lg mb-1" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                  已有 12,800+ 位达人正在赚取佣金
                </h3>
                <p className="text-white/80 text-sm">
                  平均月收益 ¥8,400 · 最高月收益 ¥68,000 · 零门槛免费加入
                </p>
              </div>
              <button
                onClick={() => navigate("/influencer-dashboard")}
                className="bg-white text-amber-600 px-8 py-3 rounded-xl font-bold text-sm transition-all hover:bg-white/90 cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <i className="ri-vip-crown-line mr-1"></i>立即加入，查看我的佣金
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
